import { captureException } from "@sentry/react";
import { Button, Form, Input, message, Space, theme, Typography } from "antd";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { AuthContext } from "/src/constants/contexts";
// @ts-ignore
import useOrg from "../../hooks/apis/useOrg";
import usePhoneLogin from "../../hooks/apis/usePhoneLogin";
import usePhoneLoginVerify from "../../hooks/apis/usePhoneLoginVerify";
import { getErrorMessage } from "../../utils/http";
import { trackLogin, trackSignup } from "../../utils/gtm";
import Error from "../error";
import Loading from "../loading";
import { filterToDigits, isPhoneNumberValid } from "/src/utils/digits";
import useWebOtp from "../../hooks/useWebOtp";

const LOGIN_OTP_TTL_SECONDS = 5 * 60;

function PhoneLoginButton({ compact = false }: { compact?: boolean }) {
  const { isLoading: orgLoading, error: orgError } = useOrg();
  // @ts-ignore
  const { handleToken } = useContext(AuthContext);
  const phoneLoginMutation = usePhoneLogin();
  const phoneLoginVerifyMutation = usePhoneLoginVerify();
  const {
    token: { colorText, colorTextSecondary, colorFillSecondary },
  } = theme.useToken();
  const [showOTP, setShowOTP] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [timer, setTimer] = useState(LOGIN_OTP_TTL_SECONDS);
  const [messageApi, contextHolder] = message.useMessage();
  const [phoneForm] = Form.useForm();
  const [otpForm] = Form.useForm();
  const submittingRef = useRef(false);
  const phoneNumberRef = useRef("");

  // Countdown timer for OTP resend
  useEffect(() => {
    if (timer > 0 && showOTP) {
      const interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [timer, showOTP]);

  if (orgLoading) return <Loading size="screen" />;
  if (orgError) return <Error size="screen" />;

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const handleOTPSubmit = useCallback(async ({ code }: { code: string }) => {
    if (submittingRef.current) return;

    submittingRef.current = true;
    try {
      const redirectPath = sessionStorage.getItem("post_login_redirect");
      const source = redirectPath?.startsWith("/marketplace")
        ? "MARKETPLACE"
        : redirectPath?.startsWith("/videos")
        ? "UPLOAD"
        : undefined;
      const { token, isNewUser } = await phoneLoginVerifyMutation.trigger({
        phoneNumber: phoneNumberRef.current,
        code,
        source,
      });
      if (isNewUser) trackSignup('phone');
      else trackLogin('phone');
      messageApi.open({
        type: "success",
        content: "خوش آمدید!",
      });
      handleToken(token);
    } catch (error: any) {
      captureException(error);
      const errorMessage = getErrorMessage(error, "خطایی رخ داده است");
      messageApi.open({
        type: "error",
        content: errorMessage,
      });
    } finally {
      submittingRef.current = false;
    }
  }, [handleToken, messageApi, phoneLoginVerifyMutation]);

  const handleOtpCodeInput = useCallback((value: string) => {
    const code = filterToDigits(value).slice(0, 6) as string;
    otpForm.setFieldValue("code", code);
    if (code.length === 6) void handleOTPSubmit({ code });
  }, [handleOTPSubmit, otpForm]);

  const { start: startWebOtp, stop: stopWebOtp } = useWebOtp({
    enabled: showOTP,
    onCode: handleOtpCodeInput,
  });

  const handlePhoneSubmit = async ({ phoneNumber: phone }: { phoneNumber: string }) => {
    const filteredPhone: string = filterToDigits(phone) as string;
    phoneNumberRef.current = filteredPhone;
    setPhoneNumber(filteredPhone);
    setShowOTP(true);
    setTimer(LOGIN_OTP_TTL_SECONDS);
    startWebOtp();
    try {
      await phoneLoginMutation.trigger(filteredPhone);
      messageApi.open({
        type: "success",
        content: "کد تایید به شماره همراه شما ارسال شد",
      });
    } catch (error: any) {
      captureException(error);
      const errorData = error?.response?.data;
      let errorMessage = getErrorMessage(error, "خطا در ارسال کد تایید");
      // If an active OTP already exists, still show the form
      if (errorData?.error === "OTP_ALREADY_SENT") {
        errorMessage = "کد تایید فعالی وجود دارد. لطفاً از کد قبلی استفاده کنید.";
      } else {
        stopWebOtp();
        setShowOTP(false);
        setPhoneNumber("");
        phoneNumberRef.current = "";
        otpForm.resetFields();
      }
      messageApi.open({
        type: "error",
        content: errorMessage,
      });
    }
  };

  const handleBackToPhone = () => {
    stopWebOtp();
    setShowOTP(false);
    setPhoneNumber("");
    phoneNumberRef.current = "";
    otpForm.resetFields();
  };

  return (
    <>
      {contextHolder}
      <div className="flex flex-col items-center w-full gap-8">
        {(!compact || showOTP) && (
          <>
            <Typography.Title
              level={2}
              className="text-center"
              style={{
                color: colorText,
                fontWeight: 700,
                fontSize: 32,
                marginBottom: 0,
              }}
            >
              {showOTP ? "تایید شماره همراه" : "ورود به حساب کاربری"}
            </Typography.Title>
            <Typography.Text
              className="text-center"
              style={{
                color: colorTextSecondary,
                fontSize: 18,
                marginBottom: 0,
              }}
            >
              {showOTP
                ? `کد تایید ۶ رقمی به شماره ${phoneNumber} ارسال شد`
                : "برای ادامه شماره همراه خود را وارد کنید"}
            </Typography.Text>
          </>
        )}

        {/* Phone Input Form */}
        {!showOTP && (
          <div className="w-full flex flex-col items-center gap-6">
            <Form
              form={phoneForm}
              layout="vertical"
              className="w-full"
              onFinish={handlePhoneSubmit}
              variant='filled'
            >
              <Form.Item
                // label="شماره همراه"
                name="phoneNumber"
                rules={[
                  { required: true, message: "این فیلد ضروری است" },
                  {
                    message: "شماره همراه معتبر نیست",
                    validator: (_, value) =>
                      !isPhoneNumberValid(value) ? Promise.reject() : Promise.resolve(),
                  },
                ]}
              >
                <Input
                  className="h-12 text-center text-lg"
                  style={{
                    direction: "ltr",
                    backgroundColor: colorFillSecondary,
                  }}
                  placeholder="09123456789"
                />
              </Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                loading={phoneLoginMutation.isMutating}
                block
                size="large"
              >
                ارسال کد تایید
              </Button>
            </Form>
          </div>
        )}

        {/* OTP Input Form */}
        {showOTP && (
          <div className="w-full flex flex-col items-center gap-6">
            <Typography.Text
              className="text-center -mt-6"
              style={{
                color: colorTextSecondary,
                fontSize: 16,
              }}
            >
              لطفا برای تایید شماره همراه کد را وارد کنید.
            </Typography.Text>
            <Form
              form={otpForm}
              layout="vertical"
              className="w-full flex-col flex items-center justify-center"
              onFinish={handleOTPSubmit}
              variant='filled'
            >
              <Form.Item
                name="code"
                className="w-full"
                rules={[
                  { required: true, message: "لطفا کد تایید را وارد کنید" },
                  {
                    pattern: /^\d{6}$/,
                    message: "کد تایید باید ۶ رقم باشد",
                  },
                ]}
              >
                <Input
                  type="tel"
                  maxLength={6}
                  name="one-time-code"
                  style={{
                    direction: "ltr",
                    textAlign: "center",
                    fontSize: "20px",
                    letterSpacing: "8px",
                    backgroundColor: colorFillSecondary,
                  }}
                  className="h-12 text-center text-lg w-full"
                  placeholder="------"
                  autoFocus
                  autoComplete="one-time-code"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  onInput={(e) => handleOtpCodeInput(e.currentTarget.value)}
                  onChange={(e) => {
                    handleOtpCodeInput(e.target.value);
                  }}
                />
              </Form.Item>
              <Button
                className="mx-auto"
                type="link"
                onClick={() => handlePhoneSubmit({ phoneNumber })}
                disabled={timer > 0 || phoneLoginMutation.isMutating}
                style={{ padding: 0, marginBottom: 16 }}
              >
                {timer > 0 ? `ارسال مجدد کد (${formatTime(timer)})` : "ارسال مجدد کد تایید"}
              </Button>
              <Space direction="vertical" style={{ width: "100%" }} size="middle">
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={phoneLoginVerifyMutation.isMutating}
                  block
                  size="large"
                >
                  تایید
                </Button>
                <Button size="large" onClick={handleBackToPhone} block>
                  تغییر شماره همراه
                </Button>
              </Space>
            </Form>
          </div>
        )}
      </div>
    </>
  );
}

export default PhoneLoginButton;
